import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { Database, Building, CheckCircle, AlertCircle, Loader2, Play, Pause, RotateCcw } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

interface CompanyProgress {
  company: string;
  company_id: string;
  status: 'pending' | 'importing' | 'completed' | 'failed';
  currentPage: number;
  totalImported: number;
  lastError?: string;
}

interface ImportConfig {
  batchSize: number;
  maxPages: number | null;
  delayBetweenBatches: number;
  companies: string[];
}

const FullHistoricalImportPage = () => {
  const { theme } = useTheme();
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState<CompanyProgress[]>([]);
  const [config, setConfig] = useState<ImportConfig>({
    batchSize: 100,
    maxPages: null,
    delayBetweenBatches: 1000,
    companies: ['hales-ac', 'climate-control', 'abraham-ac']
  });
  const [totalStats, setTotalStats] = useState({
    totalImported: 0,
    totalFailed: 0,
    estimatedTimeRemaining: '...'
  });

  // Use ref for immediate pause checking
  const pausedRef = useRef(false);

  useEffect(() => {
    const companyNames = {
      'hales-ac': 'Hales AC',
      'climate-control': 'Climate Control Services',
      'abraham-ac': 'Abraham AC'
    };
    
    setProgress(config.companies.map(id => ({
      company: companyNames[id as keyof typeof companyNames] || id,
      company_id: id,
      status: 'pending',
      currentPage: 0,
      totalImported: 0
    })));
  }, [config.companies]);

  const startFullImport = async () => {
    setImporting(true);
    pausedRef.current = false;
    
    try {
      for (const companyId of config.companies) {
        if (pausedRef.current) break;
        await importCompanyData(companyId);
      }
    } catch (error: any) {
      console.error('Import error:', error);
    } finally {
      setImporting(false);
    }
  };

  const importCompanyData = async (companyId: string) => {
    setProgress(prev => prev.map(p => 
      p.company_id === companyId ? { ...p, status: 'importing' } : p
    ));

    try {
      let page = 0;
      let hasMore = true;
      const startTime = Date.now();

      while (hasMore && !pausedRef.current) {
        if (config.maxPages !== null && page >= config.maxPages) break;

        const { data, error } = await supabase.functions.invoke('successware-import', {
          body: {
            company_id: companyId,
            import_type: 'customers',
            batch_size: config.batchSize
          }
        });

        if (error) {
          console.error('Edge function error:', error);
          throw new Error(`Edge function failed: ${error.message || JSON.stringify(error)}`);
        }

        if (!data || !data.success) {
          const errorMsg = data?.error || 'Unknown error from edge function';
          console.error('Import failed:', errorMsg);
          throw new Error(errorMsg);
        }

        setProgress(prev => prev.map(p => 
          p.company_id === companyId ? {
            ...p,
            currentPage: page + 1,
            totalImported: p.totalImported + (data?.imported_count || 0)
          } : p
        ));

        const elapsedSeconds = (Date.now() - startTime) / 1000;
        const recordsPerSecond = (data?.total_imported || 0) / elapsedSeconds;
        
        setTotalStats(prev => ({
          totalImported: prev.totalImported + (data?.imported_count || 0),
          totalFailed: prev.totalFailed + (data?.failed_count || 0),
          estimatedTimeRemaining: recordsPerSecond > 0 
            ? `~${Math.ceil((data?.remaining_estimate || 0) / recordsPerSecond / 60)} min`
            : '...'
        }));

        hasMore = data?.has_more || false;
        page++;

        if (hasMore && config.delayBetweenBatches > 0) {
          await new Promise(resolve => setTimeout(resolve, config.delayBetweenBatches));
        }

        // Check pause status after delay
        if (pausedRef.current) break;
      }

      setProgress(prev => prev.map(p => 
        p.company_id === companyId ? { ...p, status: pausedRef.current ? 'pending' : 'completed' } : p
      ));

    } catch (error: any) {
      console.error(`Import failed for ${companyId}:`, error);
      setProgress(prev => prev.map(p => 
        p.company_id === companyId ? { ...p, status: 'failed', lastError: error.message } : p
      ));
    }
  };

  const pauseImport = () => {
    pausedRef.current = true;
    setImporting(false);
  };

  const resetImport = () => {
    pausedRef.current = false;
    setProgress(prev => prev.map(p => ({
      ...p,
      status: 'pending',
      currentPage: 0,
      totalImported: 0,
      lastError: undefined
    })));
    setTotalStats({
      totalImported: 0,
      totalFailed: 0,
      estimatedTimeRemaining: '...'
    });
    setImporting(false);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.colors.bgPage, color: theme.colors.textPrimary }}>
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ color: theme.colors.textPrimary }}>
            Full Historical Import
          </h1>
          <p style={{ color: theme.colors.textSecondary }}>
            Import entire SuccessWare database with automated batch processing
          </p>
        </div>

        {/* Configuration Panel */}
        <div className="rounded-lg p-6 mb-6" style={{ backgroundColor: theme.colors.bgCard }}>
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <Database className="h-5 w-5 mr-2" style={{ color: theme.colors.primaryColor }} />
            Import Configuration
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                Batch Size (records per batch)
              </label>
              <input
                type="number"
                value={config.batchSize}
                onChange={(e) => setConfig({ ...config, batchSize: parseInt(e.target.value) || 100 })}
                disabled={importing}
                className="w-full rounded px-3 py-2 disabled:opacity-50"
                style={{
                  backgroundColor: theme.colors.bgCard,
                  border: `1px solid ${theme.colors.borderColor}`,
                  color: theme.colors.textPrimary
                }}
                min="10"
                max="500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                Max Pages (blank = unlimited)
              </label>
              <input
                type="number"
                value={config.maxPages || ''}
                onChange={(e) => setConfig({ ...config, maxPages: e.target.value ? parseInt(e.target.value) : null })}
                disabled={importing}
                placeholder="Unlimited"
                className="w-full rounded px-3 py-2 disabled:opacity-50"
                style={{
                  backgroundColor: theme.colors.bgCard,
                  border: `1px solid ${theme.colors.borderColor}`,
                  color: theme.colors.textPrimary
                }}
                min="1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                Delay Between Batches (ms)
              </label>
              <input
                type="number"
                value={config.delayBetweenBatches}
                onChange={(e) => setConfig({ ...config, delayBetweenBatches: parseInt(e.target.value) || 1000 })}
                disabled={importing}
                className="w-full rounded px-3 py-2 disabled:opacity-50"
                style={{
                  backgroundColor: theme.colors.bgCard,
                  border: `1px solid ${theme.colors.borderColor}`,
                  color: theme.colors.textPrimary
                }}
                min="0"
                max="5000"
              />
            </div>
          </div>

          <div className="rounded p-3 text-sm" style={{ 
            backgroundColor: theme.colors.bgHover,
            color: theme.colors.textSecondary 
          }}>
            <p className="font-semibold mb-1">Recommended Settings:</p>
            <ul className="space-y-1">
              <li>• Batch Size: 100 (balance between speed and API limits)</li>
              <li>• Max Pages: Leave blank for unlimited (full historical import)</li>
              <li>• Delay: 1000ms (1 second) to avoid rate limiting</li>
            </ul>
          </div>
        </div>

        {/* Total Stats */}
        <div className="rounded-lg p-6 mb-6" style={{ backgroundColor: theme.colors.bgCard }}>
          <h2 className="text-xl font-semibold mb-4">Overall Progress</h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-lg p-4" style={{ backgroundColor: theme.colors.bgHover }}>
              <div className="text-2xl font-bold" style={{ color: theme.colors.successText }}>
                {totalStats.totalImported.toLocaleString()}
              </div>
              <div className="text-sm" style={{ color: theme.colors.textSecondary }}>Total Imported</div>
            </div>
            <div className="rounded-lg p-4" style={{ backgroundColor: theme.colors.bgHover }}>
              <div className="text-2xl font-bold" style={{ color: theme.colors.dangerText }}>
                {totalStats.totalFailed.toLocaleString()}
              </div>
              <div className="text-sm" style={{ color: theme.colors.textSecondary }}>Failed</div>
            </div>
            <div className="rounded-lg p-4" style={{ backgroundColor: theme.colors.bgHover }}>
              <div className="text-2xl font-bold" style={{ color: theme.colors.primaryColor }}>
                {totalStats.estimatedTimeRemaining}
              </div>
              <div className="text-sm" style={{ color: theme.colors.textSecondary }}>Est. Time Remaining</div>
            </div>
          </div>
        </div>

        {/* Company Progress */}
        <div className="space-y-4 mb-6">
          {progress.map(company => (
            <div key={company.company_id} className="rounded-lg p-4" style={{ backgroundColor: theme.colors.bgCard }}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <Building className="h-5 w-5 mr-2" style={{ color: theme.colors.primaryColor }} />
                  <h3 className="font-semibold">{company.company}</h3>
                </div>
                <div className="flex items-center space-x-2">
                  {company.status === 'importing' && (
                    <Loader2 className="h-4 w-4 animate-spin" style={{ color: theme.colors.primaryColor }} />
                  )}
                  {company.status === 'completed' && (
                    <CheckCircle className="h-4 w-4" style={{ color: theme.colors.successText }} />
                  )}
                  {company.status === 'failed' && (
                    <AlertCircle className="h-4 w-4" style={{ color: theme.colors.dangerText }} />
                  )}
                  <span className="text-sm font-medium" style={{
                    color: company.status === 'importing' ? theme.colors.primaryColor :
                           company.status === 'completed' ? theme.colors.successText :
                           company.status === 'failed' ? theme.colors.dangerText :
                           theme.colors.textSecondary
                  }}>
                    {company.status.toUpperCase()}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span style={{ color: theme.colors.textSecondary }}>Current Page:</span>
                  <span className="ml-2 font-semibold">{company.currentPage}</span>
                </div>
                <div>
                  <span style={{ color: theme.colors.textSecondary }}>Total Imported:</span>
                  <span className="ml-2 font-semibold" style={{ color: theme.colors.successText }}>
                    {company.totalImported.toLocaleString()}
                  </span>
                </div>
              </div>

              {company.lastError && (
                <div className="mt-3 rounded p-2 text-sm" style={{
                  backgroundColor: theme.colors.dangerBg,
                  border: `1px solid ${theme.colors.dangerBorder}`,
                  color: theme.colors.dangerText
                }}>
                  Error: {company.lastError}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Control Buttons */}
        <div className="flex space-x-4">
          {!importing && (
            <button
              onClick={startFullImport}
              className="flex-1 px-6 py-3 rounded-lg transition-colors flex items-center justify-center font-semibold"
              style={{
                backgroundColor: '#10b981',
                color: '#ffffff'
              }}
              onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
              onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
            >
              <Play className="h-5 w-5 mr-2" />
              Start Full Import
            </button>
          )}

          {importing && (
            <button
              onClick={pauseImport}
              className="flex-1 px-6 py-3 rounded-lg transition-colors flex items-center justify-center font-semibold"
              style={{
                backgroundColor: '#f59e0b',
                color: '#ffffff'
              }}
              onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
              onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
            >
              <Pause className="h-5 w-5 mr-2" />
              Pause Import
            </button>
          )}

          <button
            onClick={resetImport}
            disabled={importing}
            className="flex-1 px-6 py-3 rounded-lg transition-colors flex items-center justify-center font-semibold disabled:opacity-50"
            style={{
              backgroundColor: theme.colors.bgHover,
              color: theme.colors.textPrimary
            }}
            onMouseOver={(e) => !importing && (e.currentTarget.style.opacity = '0.9')}
            onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
          >
            <RotateCcw className="h-5 w-5 mr-2" />
            Reset
          </button>
        </div>

        {/* Warning */}
        <div className="mt-6 rounded-lg p-4" style={{
          backgroundColor: theme.colors.warningBg,
          border: `1px solid ${theme.colors.warningBorder}`
        }}>
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 mr-3 mt-0.5 flex-shrink-0" style={{ color: theme.colors.warningText }} />
            <div className="text-sm" style={{ color: theme.colors.textPrimary }}>
              <p className="font-semibold mb-1">Important Notes:</p>
              <ul className="space-y-1">
                <li>• This will import ALL historical customer data from SuccessWare</li>
                <li>• The process may take several hours depending on database size</li>
                <li>• You can pause at any time - the import will stop after current batch completes</li>
                <li>• Data is automatically deduplicated (safe to run multiple times)</li>
                <li>• Click Reset to start fresh or continue where you left off</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FullHistoricalImportPage;
